<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <div
      data-testid="malware-scan-modal-warning"
      class="warning-block px-3 py-2 mb-6 d-flex align-start"
    >
      <v-icon size="24" icon="$warning" class="mt-1 mr-2 flex-shrink-0" />
      <span class="body2" v-html="$t(`modals.${config.action}.scanWarning`)" />
    </div>
    <v-select
      v-model="localValue.item.osType"
      :items="operationSystems"
      density="comfortable"
      class="mb-10 w-30"
      hide-details
      :disabled="config.action === MalwareScanActions.EDIT"
      item-value="value"
      item-title="label"
      :label="$t(`modals.${config.action}.selectOs`)"
      data-testid="malware-scan-modal-os-select"
    >
    </v-select>
    <div class="subtitle1 mb-4">
      {{ $t(`modals.${config.action}.selectScanFrequencyAndTime`) }}
    </div>
    <div class="d-flex mb-11">
      <v-select
        v-model="localValue.item.daysCount"
        :items="days"
        density="comfortable"
        class="mr-6 period-select"
        hide-details
        :label="$t(`modals.${config.action}.every`)"
        data-testid="malware-scan-modal-frequency-select"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :title="$t('general.everyXDays', { n: item.value }, item.value)"
          />
        </template>
        <template v-slot:selection="{ item }">
          {{ $t("general.everyXDays", { n: item.value }, item.value) }}
        </template>
      </v-select>
      <v-select
        v-model="localValue.item.preferredHour"
        :items="timeValues"
        density="comfortable"
        class="period-select"
        hide-details
        item-title="displayValue"
        item-value="value"
        :label="$t(`modals.${config.action}.preferredTime`)"
      >
      </v-select>
    </div>
    <v-autocomplete
      v-model="localValue.item.labels"
      :items="labels"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      :label="localValue.item.labels.length ? $t(`modals.${config.action}.labelsLabel`) : ''"
      :placeholder="$t(`modals.${config.action}.labelsPlaceholder`)"
      item-title="name"
      return-object
      chips
      multiple
      closable-chips
      clear-on-select
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          :color="item.raw.color"
          close-icon="$closeCircle"
          :text="item.title"
          label
          rounded="3"
        >
          <span class="coro-label text-uppercase text-primary">
            {{ item.title }}
          </span>
        </v-chip>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.name">
          <template v-slot:prepend>
            <coro-label :background-color="item.raw.color" variant="square" class="mr-2">
            </coro-label>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import { OsType } from "@/constants/devices";
import moment from "moment/moment";
import CoroLabel from "@/components/CoroLabel.vue";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { MalwareScanItem } from "@/_store/endpoint-security/malware-scan.module";
import camelCase from "lodash/camelCase";
import {
  MalwareScanActions,
  MALWARE_SCAN_FREQUENCY_OPTIONS,
} from "@/constants/endpoint-security-malware-scan";
import { atLeastOneIsRequired } from "@/_helpers/validators";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";

export default defineComponent({
  components: { CoroLabel },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();
    const operationSystems = [
      { value: OsType.WINDOWS, label: i18n.t(`general.osVersion.${camelCase(OsType.WINDOWS)}`) },
      { value: OsType.MAC_OS, label: i18n.t(`general.osVersion.${camelCase(OsType.MAC_OS)}`) },
      { value: OsType.LINUX, label: i18n.t(`general.osVersion.${camelCase(OsType.LINUX)}`) },
    ];
    const localValue = ref<DialogDataConfig<MalwareScanItem>>({
      item: cloneDeep(props.config.item),
      action: props.config.action,
    });
    const valid = ref(true);
    const form = ref();
    const labels = ref<Array<DeviceLabel>>([]);
    const required = reactive([(v: string) => (!v ? i18n.t("validations.required") : true)]);
    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };
    const timeValues = computed(() => {
      return Array.from(
        {
          length: 24,
        },
        (_, hour) => {
          return {
            value: hour,
            displayValue: moment({
              hour,
              minutes: 0,
            }).format("hh:mm A"),
          };
        }
      );
    });

    const getLabels = async (searchParam: string = "") => {
      const { data } = await devicesSettingsStore.searchLabels(searchParam);
      labels.value = data;
    };

    // A schedule saved before the frequency list was shortened keeps its own value as an
    // extra option, so editing a scan never silently changes the customer's frequency.
    const initialDaysCount = localValue.value.item.daysCount;
    const days = MALWARE_SCAN_FREQUENCY_OPTIONS.includes(initialDaysCount)
      ? MALWARE_SCAN_FREQUENCY_OPTIONS
      : [...MALWARE_SCAN_FREQUENCY_OPTIONS, initialDaysCount].sort((a, b) => b - a);

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal) => {
        if (newVal === null) return;
        emit("update:valid", newVal);
      },
      { deep: true, immediate: false }
    );

    onMounted(async () => {
      await getLabels();
    });

    return {
      camelCase,
      localValue,
      getLabels,
      timeValues,
      labels,
      days,
      valid,
      form,
      required,
      validate,
      operationSystems,
      atLeastOneIsRequired,
      MalwareScanActions,
    };
  },
});
</script>

<style lang="scss" scoped>
.period-select {
  width: 172px;
}
</style>
